import React, { useEffect, useReducer } from "react";
import Context from "../context";
import styled, { ThemeProvider, createGlobalStyle } from "styled-components";
import { Helmet } from "react-helmet";
import { graphql, useStaticQuery } from "gatsby";
import "bootstrap/dist/css/bootstrap.min.css";
import { formatData } from "../util";
import {
  WhatsAppOutlined,
  PhoneOutlined,
  LoadingOutlined,
} from "@ant-design/icons";

import MobileHeader from "./mobile-header";
import DesktopHeader from "./desktop-header";
import Footer from "./footer";

// Global styles
const GlobalStyles = createGlobalStyle`
  * { font-family: 'Open Sans', sans-serif; }
  html {
    font-size: 14px;
    scroll-behavior: smooth;
    font-family: Poppins, sans-serif;
    -webkit-font-smoothing: antialiased;
    @media(min-width: 768px){ font-size: 16px; }
    @media(min-width: 1200px){ font-size: 18px; }
  }
  body {
    color: #333333;
    font-family: Poppins, sans-serif;
    background: #ffffff !important;
  }
  a {
    text-decoration: none !important;
    &:visited { color: hsl(0, 0%, 23%); }
  }
  h1, h2, h3, h4, h5, h6 { font-weight: bold; }
  button, input, select, option, textarea {
    outline: none;
    font-family: Poppins, sans-serif;
  }
  input, select, option, textarea {
    padding-left: .7rem !important;
  }
  .tl-edges { width: 100% !important; }
`;

// Layout containers
const MainCont = styled.div`
  padding-top: 95.38px;
  background: #ffffff !important;
  @media (min-width: 992px) { padding-top: 0px; }
`;

const LoadingCont = styled.div`
  min-height: 100vh;
  background-color: hsl(0, 0%, 23%);
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 2rem;
  color: #ffffff;
`;

export default ({ children, location }) => {
  const [data, setData] = useReducer(
    (current, next) => ({ ...current, ...next }),
    { loading: true, error: false, data: null }
  );

  const runtimeData = useStaticQuery(graphql`
    query {
      initial {
        data
      }
    }
  `);

  const getFeatured = async (email, id, typeId, maxProperties) => {
    try {
      const res = await fetch(
        `https://wsnzm.clasihome.com:3443/api/conv/properties?email=${email}&id=${id}&typeId=${typeId}&status=PUBLICADA&limit=${maxProperties}`
      );
      const result = await res.json();
      return result;
    } catch (e) {
      console.error("ERROR PROPIEDADES DESTACADAS", e);
    }
  };

  const handleData = async () => {
    const preview = /builderId/.test(location.search);
    const builderId =
      preview && location.search.match("[?&]builderId=([^&]+)")[1];

    try {
      if (runtimeData.initial.data) {
        const parsed = formatData(JSON.parse(runtimeData.initial.data));
        const featured = await getFeatured(
          parsed.officeId,
          parsed.typeId,
          parsed.home.properties.maxProperties
        );
        parsed.featuredProperties = featured.properties;
        setData({ loading: false, data: parsed });
      } else if (builderId) {
        const res = await fetch(
          `https://wsnzm.clasihome.com:3443/api/conv/properties`
        );
        const result = await res.json();
        const parsed = formatData(result);
        const featured = await getFeatured(
          parsed.officeId,
          parsed.typeId,
          parsed.home.properties.maxProperties
        );
        parsed.featuredProperties = featured.properties;
        setData({ loading: false, data: parsed });
      } else {
        const parsed = formatData({ office: "" });
        const featured = await getFeatured(
          parsed.officeId,
          parsed.typeId,
          parsed.home.properties.maxProperties
        );
        parsed.featuredProperties = featured.properties;
        setData({ loading: false, data: parsed });
      }
    } catch (e) {
      console.error("ERROR", e);
      setData({ loading: false, error: e });
    }
  };

  useEffect(() => {
    handleData();
  }, []);

  if (data.loading) {
    return (
      <LoadingCont>
        <LoadingOutlined spin />
      </LoadingCont>
    );
  }

  if (data.error) {
    return <LoadingCont>Error de conexión</LoadingCont>;
  }

  return (
    <Context.Provider value={data.data}>
      <ThemeProvider theme={data.data}>
        <MainCont>
          <Helmet>
            <meta charSet="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <title>PM Asesorías Inmobiliarias</title>
            <meta name="description" content="Ayudamos a personas y hogares a encontrarse. Gestión inmobiliaria, arriendos y asesoría financiera." />
            <meta name="keywords" content="Paola Muñoz, PM Asesorias, Propiedades, Corretaje, Inmobiliaria" />
            <link rel="canonical" href="https://www.pmasesoriasinmobiliarias.cl/" />

            {/* Open Graph */}
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://www.pmasesoriasinmobiliarias.cl/" />
            <meta property="og:title" content="PM Asesorías Inmobiliarias" />
            <meta property="og:description" content="Ayudamos a personas y hogares a encontrarse. Gestión inmobiliaria, arriendos y asesoría financiera." />
            <meta property="og:image" content="https://www.pmasesoriasinmobiliarias.cl/assets/preview.jpg" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="theme-color" content={data.data.primaryColor} />
          </Helmet>

          <GlobalStyles />
          <MobileHeader />
          <DesktopHeader />
          {children}
          <Footer />
        </MainCont>
      </ThemeProvider>
    </Context.Provider>
  );
};